/**
 * proceduresAdmin.service.ts — Admin > Products > Procedures (legacy
 * admin_procedures_list.php): the account's category > sub-category >
 * procedure tree, through /procedure-admin. Writes need Admin Access.
 *
 * Naming trap: plans, leads and tasks call the SUB-CATEGORY "the procedure";
 * the procedure here is its priced variant, which quotes use.
 */
import { apiClient } from '@/services/apiClient';
import type { ApiResponse } from '@/types/api.types';

export type ProcedureLevel = 'categories' | 'subcategories' | 'procedures';

export interface SubcategoryDocument {
  /** 0 for a row not saved yet. */
  id: number;
  /** The packet slot (1–300) this document fills. */
  slot: number;
  documentId: number;
}

export interface AdminProcedure {
  id: number;
  subcategoryId: number;
  name: string;
  /** Legacy's "Description". */
  laymansTerms: string;
  diagnosis: string;
  feeToPatient: string;
  anesthesiaFee: string;
  operatingRoomFee: string;
  /** "0" for none, else hours such as "1.50". */
  expNumHours: string;
  hcpcCode: string;
  sameAsStd: boolean;
  setAsStd: boolean;
  active: boolean;
  order: number;
}

export interface AdminProcedureSubcategory {
  id: number;
  categoryId: number;
  name: string;
  hint: string;
  active: boolean;
  order: number;
  documents: SubcategoryDocument[];
  procedures: AdminProcedure[];
}

export interface AdminProcedureCategory {
  id: number;
  name: string;
  description: string;
  hint: string;
  active: boolean;
  order: number;
  subcategories: AdminProcedureSubcategory[];
}

export interface ProcedureTree {
  categories: AdminProcedureCategory[];
  /** The Hours list runs to this (never below 10). */
  maxHours: number;
  /** The account has imported I&E content: show its editors and the standard flags. */
  hasIE: boolean;
}

export const PROCEDURE_NAME_MAX = 255;
export const DOCUMENT_SLOT_MAX = 300;

export function getProcedureTree(): Promise<ProcedureTree> {
  return apiClient.get<ApiResponse<ProcedureTree>>('/procedure-admin').then((r) => {
    const d = r.data.data;
    return { categories: d?.categories ?? [], maxHours: Number(d?.maxHours) || 10, hasIE: d?.hasIE === true };
  });
}

/** Legacy's Hours choices: none, then quarter hours up to `max`. */
export function hourOptions(max: number): { value: string; label: string }[] {
  const options = [{ value: '0', label: 'None' }];
  for (let q = 1; q <= Math.round(max * 4); q += 1) {
    const hours = (q / 4).toFixed(2);
    options.push({ value: hours, label: hours });
  }
  return options;
}

/** A stored Hours value as its option value ("1.5" → "1.50", blank → "0"). */
export function hoursValue(stored: string): string {
  const n = Number(stored);
  return Number.isFinite(n) && n > 0 ? n.toFixed(2) : '0';
}

export interface CategoryPayload {
  name: string;
  description: string;
  hint: string;
}

export interface SubcategoryPayload {
  categoryId: number;
  name: string;
  hint: string;
  documents: SubcategoryDocument[];
}

export type ProcedurePayload = Omit<AdminProcedure, 'id' | 'active' | 'order'>;

type Payload = CategoryPayload | SubcategoryPayload | ProcedurePayload;

function body(level: ProcedureLevel, p: Payload): Record<string, unknown> {
  const out: Record<string, unknown> = { ...p, name: p.name.trim() };
  if (level === 'subcategories') {
    out.documents = (p as SubcategoryPayload).documents.map((d) => ({ id: d.id || null, slot: d.slot, documentId: d.documentId }));
  }
  return out;
}

export function createProcedureRow(level: ProcedureLevel, p: Payload): Promise<number> {
  return apiClient
    .post<ApiResponse<{ id: number }>>(`/procedure-admin/${level}`, body(level, p))
    .then((r) => Number(r.data.data?.id) || 0);
}

export function updateProcedureRow(level: ProcedureLevel, id: number, p: Payload): Promise<void> {
  return apiClient.put(`/procedure-admin/${level}/${id}`, body(level, p)).then(() => undefined);
}

export function deleteProcedureRow(level: ProcedureLevel, id: number): Promise<void> {
  return apiClient.delete(`/procedure-admin/${level}/${id}`).then(() => undefined);
}

export function restoreProcedureRow(level: ProcedureLevel, id: number): Promise<void> {
  return apiClient.post(`/procedure-admin/${level}/${id}/restore`).then(() => undefined);
}

/** The live rows of one parent in their new order (no parent for categories). */
export function reorderProcedureRows(level: ProcedureLevel, ids: number[], parentId?: number): Promise<void> {
  return apiClient
    .post(`/procedure-admin/${level}/reorder`, parentId === undefined ? { ids } : { ids, parentId })
    .then(() => undefined);
}

/** A row of legacy's HCPCS Directory: Code, Summary (short) and Description (long). */
export interface HcpcCode {
  code: string;
  summary: string;
  description: string;
}

/** Rows on a page of the directory (the API's page size, legacy's 50). */
export const HCPC_PAGE_SIZE = 50;

export interface HcpcPage {
  codes: HcpcCode[];
  /** Matches across every page. */
  total: number;
}

/**
 * Legacy's HCPCS Directory: every code before a search; with one, every word
 * must match the code or the summary. `page` is 1-based, 50 codes a page.
 */
export function searchHcpcCodes(search: string, page = 1): Promise<HcpcPage> {
  return apiClient
    .get<ApiResponse<HcpcCode[]> & { meta?: { total?: number } }>('/procedure-admin/hcpc-codes', { params: { search, page } })
    .then((r) => ({ codes: r.data.data ?? [], total: Number(r.data.meta?.total ?? 0) }));
}

// ─── I&E document content ───────────────────────────────────────────────────

/** Legacy's dataType: 1 belongs to a sub-category, the rest to a procedure. */
export type IeType = 1 | 2 | 3 | 4 | 5 | 6;

export const IE_TYPES: { type: IeType; label: string }[] = [
  { type: 1, label: 'Inquiry' },
  { type: 2, label: 'Risk' },
  { type: 3, label: 'Pre-Op' },
  { type: 4, label: 'Post-Op' },
  { type: 5, label: 'As You Heal' },
  { type: 6, label: 'Special' },
];

export interface IeParagraph {
  /** 0 for one not saved yet. */
  id: number;
  title: string;
  bold: boolean;
  box: boolean;
  italic: boolean;
  content: string;
}

export interface IeSection {
  id: number;
  name: string;
  paragraphs: IeParagraph[];
}

export function getIeContent(type: IeType, ownerId: number): Promise<IeSection[]> {
  return apiClient
    .get<ApiResponse<IeSection[]>>(`/procedure-admin/ie/${type}/${ownerId}`)
    .then((r) => r.data.data ?? []);
}

/** The whole content, in order: what is left out is removed. */
export function saveIeContent(type: IeType, ownerId: number, sections: IeSection[]): Promise<IeSection[]> {
  return apiClient
    .put<ApiResponse<IeSection[]>>(`/procedure-admin/ie/${type}/${ownerId}`, {
      sections: sections.map((s) => ({
        id: s.id || null,
        name: s.name.trim(),
        paragraphs: s.paragraphs.map((p) => ({ ...p, id: p.id || null, title: p.title.trim() })),
      })),
    })
    .then((r) => r.data.data ?? []);
}
